import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { tokens } from '../theme/tokens';
import { BRAND_NAME } from '../components/Logo';
import { PageShell } from '../components/Layout';
import ScrollFade from '../components/ScrollFade';
import { DemoSignInModal } from '../components/DemoSignInModal';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useFlowClient } from '../hooks/useFlowClient';
import { useWeeklyData } from '../hooks/useWeeklyData';
import { useDemoGuard } from '../hooks/useDemoGuard';
import { trackEvent } from '../utils/clarity';
import { ExportStats, Activity } from '../types';
import { getTwoWeekLookaheadRange, isInRange, formatDate } from '../utils/dateUtils';
import { fmtShort, durationDays } from '../components/WeeklyGrid';

type Period = 'week' | 'month';

interface ReportState {
  loading: boolean;
  result: string | null;
  error: string | null;
  elapsed: number;
}

interface ExportState {
  loading: boolean;
  filelink: string | null;
  stats: ExportStats | null;
  error: string | null;
}

const EMPTY_EXPORT: ExportState = { loading: false, filelink: null, stats: null, error: null };

const ReportCard: React.FC<{
  label: string;
  description: string;
  emoji: string;
  period: Period;
  onGenerate: (period: Period) => void;
  state: ReportState;
}> = ({ label, description, emoji, period, onGenerate, state }) => (
  <div className="bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 p-4">
    <div className="flex items-start justify-between gap-3 mb-3">
      <div>
        <div className="flex items-center gap-2">
          <span className="text-xl">{emoji}</span>
          <h3 className="text-sm font-semibold text-text dark:text-gray-100">{label}</h3>
        </div>
        <p className="text-xs text-muted dark:text-gray-400 mt-0.5">{description}</p>
      </div>
      <button
        onClick={() => onGenerate(period)}
        disabled={state.loading}
        className="shrink-0 bg-accent hover:bg-link disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-[8px] transition-colors min-h-[44px]"
      >
        {state.loading ? '…' : 'Generate'}
      </button>
    </div>

    {state.loading && (
      <div className="bg-tint rounded-[8px] p-3 fade-in">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-sm font-medium text-accent">Generating report…</p>
        </div>
        <p className="text-xs text-muted">Analysing schedule data. This may take up to 20 seconds.</p>
      </div>
    )}

    {state.error && !state.loading && (
      <div className="bg-crit-bg border border-crit-text rounded-[8px] p-3">
        <p className="text-sm text-crit-text">{state.error}</p>
      </div>
    )}

    {state.result && !state.loading && (
      <div className="bg-ground dark:bg-gray-700 rounded-[8px] p-3 fade-in">
        <div className="flex justify-between items-center mb-2">
          <p className="text-xs font-semibold text-muted dark:text-gray-400 uppercase tracking-wide">Report ready</p>
          <button
            onClick={() => {
              const blob = new Blob([state.result!], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${label.replace(/\s+/g, '_')}_report.txt`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="text-xs text-accent dark:text-green-400 font-semibold underline"
          >
            Download
          </button>
        </div>
        <div className="max-h-48 overflow-y-auto scrollbar-hide ask-answer text-sm">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{state.result}</ReactMarkdown>
        </div>
      </div>
    )}
  </div>
);

const ExportStatsCard: React.FC<{ stats: ExportStats }> = ({ stats }) => {
  const [showWarnings, setShowWarnings] = useState(false);
  return (
    <div className="mt-3 rounded-[8px] border border-gray-100 dark:border-gray-600 p-3 bg-ground dark:bg-gray-750">
      <p className="text-xs font-semibold text-muted dark:text-gray-400 uppercase tracking-wide mb-2">Export summary</p>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[
          ['Activities', stats.activities],
          ['Relationships', stats.relationships],
          ['Resources', stats.resources],
          ['Calendars', stats.calendars],
          ['Activity codes', stats.activityCodes],
        ].map(([label, val]) => (
          <div key={label as string} className="bg-white dark:bg-gray-800 rounded-[6px] p-2 border border-gray-100 dark:border-gray-600">
            <p className="text-xs text-muted dark:text-gray-400">{label}</p>
            <p className="text-sm font-semibold text-text dark:text-gray-100">{val}</p>
          </div>
        ))}
        <div className="bg-white dark:bg-gray-800 rounded-[6px] p-2 border border-gray-100 dark:border-gray-600">
          <p className="text-xs text-muted dark:text-gray-400">Warnings</p>
          <p className={`text-sm font-semibold ${stats.warnings > 0 ? 'text-crit-text' : 'text-accent'}`}>{stats.warnings}</p>
        </div>
      </div>
      {stats.warnings > 0 && (
        <div className="mt-2">
          <button
            onClick={() => setShowWarnings(!showWarnings)}
            className="text-xs text-amber-700 dark:text-amber-400 font-semibold underline"
          >
            {showWarnings ? 'Hide warnings' : `Show ${stats.warnings} warning${stats.warnings > 1 ? 's' : ''}`}
          </button>
          {showWarnings && (
            <div className="mt-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-[6px] p-2">
              {stats.warningMessages.map((w, i) => (
                <p key={i} className="text-xs text-amber-800 dark:text-amber-300">{w}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ExportCard: React.FC<{
  title: string;
  subtitle: string;
  description: string;
  buttonLabel: string;
  loadingLabel: string;
  recommended?: boolean;
  state: ExportState;
  onExport: () => void;
}> = ({ title, subtitle, description, buttonLabel, loadingLabel, recommended, state, onExport }) => (
  <div className="flex-1 bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 p-4">
    <div className="flex items-start gap-2 mb-2">
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-text dark:text-gray-100">{title}</h3>
          {recommended && (
            <span className="text-xs font-semibold text-white bg-accent px-2 py-0.5 rounded-full">Recommended</span>
          )}
        </div>
        <p className="text-xs text-muted dark:text-gray-400">{subtitle}</p>
      </div>
    </div>
    <p className="text-xs text-muted dark:text-gray-400 mb-3">{description}</p>
    <button
      onClick={onExport}
      disabled={state.loading}
      className="w-full bg-accent hover:bg-link disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-[8px] transition-colors min-h-[44px]"
    >
      {state.loading ? loadingLabel : buttonLabel}
    </button>

    {state.loading && (
      <div className="flex items-center gap-2 mt-3 text-sm text-muted dark:text-gray-400">
        <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
        <span>{loadingLabel}</span>
      </div>
    )}

    {state.error && !state.loading && (
      <div className="mt-3 bg-crit-bg border border-crit-text rounded-[8px] p-3">
        <p className="text-sm text-crit-text">{state.error}</p>
      </div>
    )}

    {state.filelink && !state.loading && (
      <div className="mt-3 bg-tint rounded-[8px] p-3 fade-in">
        <p className="text-xs font-semibold text-accent mb-1">Export ready</p>
        <a
          href={state.filelink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-accent font-semibold underline break-all"
        >
          Download {title} →
        </a>
        {state.stats && <ExportStatsCard stats={state.stats} />}
      </div>
    )}
  </div>
);

// ── 2 Week Lookahead ────────────────────────────────────────────────────────
// Straight data table, no Claude call — this week's Monday through the Sunday two weeks
// out (three full calendar weeks: this week + next 2). Filter matches Weekly Lookahead's
// own overlap convention: an activity is included if its Start Date OR Finish Date falls
// within the window, so activities spanning into or through it are caught, not just ones
// starting exactly in range.
function sortByStartDate(a: Activity, b: Activity): number {
  const as = (a.start_date ?? '').substring(0, 10);
  const bs = (b.start_date ?? '').substring(0, 10);
  return as < bs ? -1 : as > bs ? 1 : 0;
}

const TwoWeekLookaheadCard: React.FC<{
  projectName: string;
  activities: Activity[];
  loading: boolean;
  error: string | null;
}> = ({ projectName, activities, loading, error }) => {
  const [expanded, setExpanded] = useState(false);
  const { start, end } = useMemo(() => getTwoWeekLookaheadRange(), []);
  // This PDF is generated entirely client-side (jsPDF, no /api call) — there's no server
  // rejection to fall back on, so it must be guarded here directly rather than relying
  // on a 403 like Export/Report do.
  const { gateOpen, gateFeature, closeGate, guard } = useDemoGuard();

  const rows = useMemo(
    () =>
      activities
        .filter((a) => isInRange(a.start_date, start, end) || isInRange(a.finish_date, start, end))
        .sort(sortByStartDate),
    [activities, start, end]
  );

  const handleDownloadPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });

    // Flotillo header: the onLight mark drawn with jsPDF shapes (96×64 viewBox scaled to
    // 12mm wide — under the size where the dotted connector is dropped), then the wordmark.
    const s = 12 / 96, ox = 14, oy = 8;
    const at = (x: number) => ox + x * s, atY = (y: number) => oy + y * s;
    doc.setFillColor(tokens.logoNavy);
    doc.roundedRect(at(4), atY(2), 88 * s, 60 * s, 16 * s, 16 * s, 'F');
    doc.setFillColor(tokens.logoLight);
    doc.roundedRect(at(12), atY(12), 32 * s, 8 * s, 4 * s, 4 * s, 'F');
    doc.roundedRect(at(24), atY(26), 32 * s, 8 * s, 4 * s, 4 * s, 'F');
    doc.setDrawColor(tokens.logoRed);
    doc.setLineWidth(3 * s);
    doc.roundedRect(at(37.5), atY(41.5), 19 * s, 6 * s, 3 * s, 3 * s, 'S');
    doc.setFillColor(tokens.logoRed);
    doc.triangle(at(80), atY(35.5), at(88.5), atY(44.5), at(71.5), atY(44.5), 'F');
    doc.triangle(at(71.5), atY(44.5), at(88.5), atY(44.5), at(80), atY(53.5), 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(tokens.ink);
    doc.text(BRAND_NAME, ox + 12 + 3, oy + 5.8);

    doc.setFontSize(13);
    doc.text(`2 Week Lookahead — ${projectName}`, 14, 23);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(tokens.muted);
    doc.text(`${formatDate(start.toISOString())} – ${formatDate(end.toISOString())}`, 14, 29);

    autoTable(doc, {
      startY: 34,
      head: [['ACTIVITY ID', 'ACTIVITY NAME', 'DURATION (DAYS)', 'START DATE', 'FINISH DATE', '% COMPLETE']],
      body: rows.map((a) => [
        a.activity_id,
        a.activity_name,
        durationDays(a.target_drtn_hr_cnt),
        fmtShort(a.start_date),
        fmtShort(a.finish_date),
        `${a.pct_complete ?? 0}%`,
      ]),
      // Flotillo table header: faint bold text on the table-head background.
      headStyles: { fillColor: tokens.tableHead, textColor: tokens.faint, fontStyle: 'bold', fontSize: 8 },
      styles: { fontSize: 9, textColor: tokens.text, lineColor: tokens.line },
      alternateRowStyles: { fillColor: tokens.ground },
    });

    doc.save(`2_Week_Lookahead_${projectName.replace(/\s+/g, '_')}.pdf`);
  };

  if (!expanded) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🗓️</span>
              <h3 className="text-sm font-semibold text-text dark:text-gray-100">2 Week Lookahead</h3>
            </div>
            <p className="text-xs text-muted dark:text-gray-400 mt-0.5">
              Straight data table of activities across this week and the next two — no AI narrative.
            </p>
          </div>
          <button
            onClick={() => setExpanded(true)}
            className="shrink-0 bg-accent hover:bg-link text-white text-sm font-semibold px-4 py-2 rounded-[8px] transition-colors min-h-[44px]"
          >
            Preview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 p-4">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xl">🗓️</span>
            <h3 className="text-sm font-semibold text-text dark:text-gray-100">2 Week Lookahead</h3>
          </div>
          <p className="text-xs text-muted dark:text-gray-400 mt-0.5">
            {formatDate(start.toISOString())} – {formatDate(end.toISOString())} · straight data table, no AI narrative
          </p>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-6 justify-center text-sm text-muted dark:text-gray-400">
          <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          Loading activities…
        </div>
      )}

      {error && !loading && (
        <div className="bg-crit-bg border border-crit-text rounded-[8px] p-3 mt-2">
          <p className="text-sm text-crit-text">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          {rows.length === 0 ? (
            <p className="text-xs text-muted dark:text-gray-400 py-4 text-center">
              No activities start or finish in this window.
            </p>
          ) : (
            // Scrolls sideways on narrow screens; Activity ID stays pinned on the left.
            <ScrollFade className="mt-3">
            <div className="overflow-x-auto overscroll-x-contain rounded-[8px] border border-gray-100 dark:border-gray-700">
              <table className="w-full min-w-[640px] text-xs">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700 text-left">
                    <th className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-700 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] py-2 px-2 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Activity ID</th>
                    <th className="py-2 px-2 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Activity Name</th>
                    <th className="py-2 px-2 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide text-center">Duration</th>
                    <th className="py-2 px-2 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide text-center">Start Date</th>
                    <th className="py-2 px-2 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide text-center">Finish Date</th>
                    <th className="py-2 px-2 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide text-center">% Complete</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.activity_id} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] py-1.5 px-2 font-mono text-muted dark:text-gray-400 whitespace-nowrap">{a.activity_id}</td>
                      <td className="py-1.5 px-2 text-text dark:text-gray-100">{a.activity_name}</td>
                      <td className="py-1.5 px-2 text-center text-muted dark:text-gray-400">{durationDays(a.target_drtn_hr_cnt)}</td>
                      <td className="py-1.5 px-2 text-center text-muted dark:text-gray-400 whitespace-nowrap">{fmtShort(a.start_date)}</td>
                      <td className="py-1.5 px-2 text-center text-muted dark:text-gray-400 whitespace-nowrap">{fmtShort(a.finish_date)}</td>
                      <td className="py-1.5 px-2 text-center text-muted dark:text-gray-400">{a.pct_complete ?? 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </ScrollFade>
          )}

          <button
            onClick={() => guard(handleDownloadPdf, 'download the 2-week lookahead PDF')}
            disabled={rows.length === 0}
            className="mt-3 w-full bg-accent hover:bg-link disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-[8px] transition-colors min-h-[44px]"
          >
            Download PDF
          </button>
        </>
      )}
      <DemoSignInModal open={gateOpen} onClose={closeGate} feature={gateFeature} />
    </div>
  );
};

const Report: React.FC = () => {
  const { project_id } = useParams<{ project_id: string }>();
  const { projects, setSelectedProject } = useApp();
  const { userEmail } = useAuth();
  const flowClient = useFlowClient();
  const { activities, loading: activitiesLoading, error: activitiesError, load: loadActivities } = useWeeklyData(userEmail, project_id);
  const { gateOpen, gateFeature, closeGate, guard } = useDemoGuard();

  const [xerState, setXerState] = useState<ExportState>(EMPTY_EXPORT);
  const [xmlState, setXmlState] = useState<ExportState>(EMPTY_EXPORT);

  const [weekState, setWeekState] = useState<ReportState>({ loading: false, result: null, error: null, elapsed: 0 });
  const [monthState, setMonthState] = useState<ReportState>({ loading: false, result: null, error: null, elapsed: 0 });

  const project = projects.find((p) => p.id === project_id);

  useEffect(() => {
    if (project) setSelectedProject(project);
  }, [project, setSelectedProject]);

  useEffect(() => { trackEvent('report_viewed'); }, []);

  // Only the 2 Week Lookahead card needs the raw activity list — loaded eagerly here
  // (same pattern as Weekly.tsx) since it's the default-visible report on this screen.
  useEffect(() => { loadActivities(); }, [loadActivities]);

  const generateReport = async (period: Period) => {
    const setState = period === 'week' ? setWeekState : setMonthState;
    setState({ loading: true, result: null, error: null, elapsed: 0 });
    try {
      const res = await flowClient.report(userEmail || '', project_id || '', period, 'owner');
      setState({ loading: false, result: res.result || 'Report generated.', error: null, elapsed: 0 });
    } catch (err) {
      setState({ loading: false, result: null, error: err instanceof Error ? err.message : 'Failed to generate report.', elapsed: 0 });
    }
  };

  const handleExport = async (format: 'xer' | 'xml') => {
    if (!userEmail || !project_id) return;
    const setState = format === 'xer' ? setXerState : setXmlState;
    setState({ loading: true, filelink: null, stats: null, error: null });
    try {
      const res = format === 'xer'
        ? await flowClient.exportXer(userEmail, project_id)
        : await flowClient.exportXml(userEmail, project_id);
      setState({ loading: false, filelink: res.filelink, stats: res.stats ?? null, error: null });
    } catch (err) {
      setState({ loading: false, filelink: null, stats: null, error: err instanceof Error ? err.message : 'Export failed.' });
    }
  };

  return (
    <PageShell className="mt-4 space-y-4 pb-10">
        {project && (
          <div className="pt-2 pb-1">
            <h1 className="h1-page break-anywhere">{project.project_name}</h1>
            <p className="text-[15px] text-muted">Schedule reports</p>
          </div>
        )}

        {/* Report cards */}
        <ReportCard emoji="📅" label="This week" description="Summary of activities starting, ongoing, and finishing this week." period="week" onGenerate={(p) => guard(() => generateReport(p), 'generate reports')} state={weekState} />
        <ReportCard emoji="📆" label="This month" description="Monthly schedule progress, milestones, and risk summary." period="month" onGenerate={(p) => guard(() => generateReport(p), 'generate reports')} state={monthState} />
        <TwoWeekLookaheadCard
          projectName={project?.project_name ?? project_id ?? 'Project'}
          activities={activities}
          loading={activitiesLoading}
          error={activitiesError}
        />

        {/* Export section — PM only; Viewer can view/generate reports above, not export */}
        {project?.role_on_project !== 'Viewer' && (
          <div className="bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-text dark:text-gray-100 mb-1">Export schedule to P6</h3>
            <p className="text-xs text-muted dark:text-gray-400 mb-4">
              Full round-trip export. Every field stored from P6 is returned to P6 exactly — zero manual re-entry.
            </p>
            <div className="flex gap-3">
              <ExportCard
                title="Export to P6 XER"
                subtitle="Native P6 format"
                description="Full round-trip. Import directly into P6 with zero manual re-entry. All activities, relationships, resources, calendars and constraints preserved."
                buttonLabel="Export XER"
                loadingLabel="Generating XER…"
                recommended
                state={xerState}
                onExport={() => guard(() => handleExport('xer'), 'export to P6')}
              />
              <ExportCard
                title="Export to P6 XML"
                subtitle="Standard XML format"
                description="P6-compatible XML. Suitable for P6 versions that do not support direct XER import or for sharing with other scheduling tools."
                buttonLabel="Export XML"
                loadingLabel="Generating XML…"
                state={xmlState}
                onExport={() => guard(() => handleExport('xml'), 'export to P6')}
              />
            </div>
          </div>
        )}
      
      <DemoSignInModal open={gateOpen} onClose={closeGate} feature={gateFeature} />
    </PageShell>
  );
};

export default Report;
